import { z } from 'zod'

/**
 * Ereignistypen des Ereignis-Logs (Konzept Kap. 8, Entscheidung E-05).
 *
 * Jede Zustandsänderung wird ein Ereignis. Alle Auswertungen in Phase 2
 * (Zeitleiste, Daily, Liegezeiten, Tages-Popup) sind Abfragen darauf – es gibt
 * keine zusätzlichen Kennzahlen-Tabellen und keine manuelle Pflege.
 *
 * Diese Liste wächst mit den Phasen. Neue Typen nur ergänzen, bestehende
 * nicht umbenennen: im Log liegen dann schon Zeilen damit.
 */
export const TASK_EVENT_TYPES = [
  /* Herkunft und Bestand */
  'imported', // Aufgabe erstmals aus einer Quelle übernommen
  'created', // in Work angelegt
  /* Lebenszyklus */
  'status_changed',
  'priority_changed',
  /** Felder der Aufgabe geändert (Titel, Beschreibung, Fälligkeit, Schätzung). */
  'updated',
  'pulled', // aus einem Pool gezogen (Kap. 4.3)
  'assigned',
  'unassigned',
  'pool_changed',
  /* Aus den Quellsystemen (Phase 1 und 3) */
  'comment',
  'label_changed', // GitLab-Label gewechselt
  'commit_referenced',
  'merge_request_referenced',
  'closed',
  'reopened',
  /**
   * **Ein Quellereignis, das Work selbst verursacht hat** (Phase 5, AP 5.3).
   *
   * Work schreibt ein Label nach GitLab; GitLab macht daraus ein
   * `resource_label_event`; der nächste Import liest es und legte daraus bisher einen
   * **zweiten** Statuswechsel an – ohne Urheber, Stunden nach der Entscheidung, und
   * gezählt in jeder Auswertung. Dieser Typ ist der Platz, den Work für dieses Ereignis
   * **vorbelegt**, bevor der Import es sieht.
   *
   * Er wird nirgends angezeigt (`istSichtbar` → `false`). Er existiert, damit die Zeile
   * belegt ist – und damit nachschlagbar bleibt, warum an dieser Stelle nichts steht.
   */
  'echo',
  /**
   * **Ein Mit-Owner schlägt eine Priorität vor** (Phase 9, Konzept Kap. 5.8).
   *
   * Der Fachbereich darf die Priorität nicht *setzen* – das wäre die Tür, durch die jede
   * Abteilung ihre eigenen Aufgaben auf „Sofort" stellt und die Steuerung nichts mehr zu
   * steuern hat. Er darf sie **vorschlagen**, mit Begründung.
   *
   * Als Ereignis und nicht als Tabelle: ein Vorschlag ist ein Vorgang, kein Zustand. Was er
   * hinterlässt, ist genau das, was das Log ohnehin führt – *wer wollte wann was, und warum*.
   * Eine eigene Tabelle müsste den Lebenszyklus zusätzlich pflegen; hier ergibt er sich aus
   * der Reihenfolge (`offenerPrioVorschlag`).
   *
   * Nutzlast: `{ prioritaet, grund }` beim Vorschlag, `{ entschieden: 'uebernommen' |
   * 'verworfen' }` bei der Antwort. **Kein `nach`** – siehe `TASK_EVENT_TYPES_MIT_ZIELSTATUS`.
   */
  'prio_vorschlag',
  /**
   * **Die Aufgabe ist in eine andere Arbeitswelt umgezogen** (Z-07.17, 26.08.2026).
   *
   * Kein `updated`, obwohl es formal ein Feld ist: ein Umzug entscheidet, **wer diese Aufgabe von
   * jetzt an überhaupt sieht**. Unter „Bearbeitet" stünde diese Auskunft neben „Titel, Fälligkeit
   * geändert", und die Frage, die unweigerlich folgt – *wo war sie vorher?* – ließe sich aus dem
   * Log nicht mehr beantworten.
   *
   * Nutzlast: `{ vonWorkspace, nachWorkspace }`, dazu `poolsGeloest` und `vorhabenGeloest`, wenn
   * beim Umzug etwas mitgelöst werden musste. **Kein `nach`** – siehe
   * {@link TASK_EVENT_TYPES_MIT_ZIELSTATUS}: das Feld gehört dem Status, und ein Workspace-Name
   * darin würde als Erledigung gezählt.
   */
  'workspace_changed',
  /**
   * **Zwei Aufgaben sind zu einer geworden** (Z-11, 28.08.2026).
   *
   * Nils: *„Habe manchmal Aufgaben die sich irgendwie doppeln, aber jede für sich ein paar Infos
   * enthält. Das wäre schön wenn man die dann zusammenführen könnte."*
   *
   * Das Ereignis steht an der **überlebenden** Aufgabe und ist der einzige Ort, an dem danach
   * noch steht, dass es die andere je gab: ihre Zeile ist gelöscht, ihr Verlauf hängt jetzt hier
   * und ist von dem eigenen nicht mehr zu unterscheiden. Ohne diese eine Zeile stünde in der
   * Aufgabe plötzlich ein zweiter Kommentarstrang, den niemand erklären kann.
   *
   * Nutzlast: `{ vonTitel, vonQuelle, vonSchluessel, kommentare, ereignisse, uebernommen[] }` –
   * Titel und Schlüssel der verschwundenen Aufgabe, dazu was mitkam. **Keine `vonId`**: die
   * Aufgabe gibt es nicht mehr, ein Verweis darauf führte ins Leere und lüde dazu ein, einen
   * Link daraus zu machen.
   *
   * **Kein `nach`** – siehe {@link TASK_EVENT_TYPES_MIT_ZIELSTATUS}.
   */
  'merged',
] as const

export type TaskEventType = (typeof TASK_EVENT_TYPES)[number]
export const taskEventTypeSchema = z.enum(TASK_EVENT_TYPES)

export const TASK_EVENT_TYPE_LABELS: Record<TaskEventType, string> = {
  imported: 'Übernommen',
  created: 'Angelegt',
  status_changed: 'Status geändert',
  priority_changed: 'Priorität geändert',
  updated: 'Bearbeitet',
  pulled: 'Gezogen',
  assigned: 'Zugewiesen',
  unassigned: 'Zuweisung entfernt',
  pool_changed: 'Pool geändert',
  comment: 'Kommentar',
  label_changed: 'Label geändert',
  commit_referenced: 'Commit',
  merge_request_referenced: 'Merge Request',
  closed: 'Geschlossen',
  reopened: 'Wieder geöffnet',
  echo: 'Von Work verursacht',
  prio_vorschlag: 'Priorität vorgeschlagen',
  workspace_changed: 'Arbeitswelt gewechselt',
  merged: 'Zusammengeführt',
}

/**
 * Ereignistypen, die **nicht angezeigt werden** – heute genau einer.
 *
 * Kein Filter in der Anzeige, sondern eine Liste an einer Stelle: eine vergessene Abfrage
 * ist ein Leck, durch das das Echo doch wieder auftaucht, und zwar dort, wo es am meisten
 * stört (Verlauf, Zeitleiste, Tages-Popup).
 */
export const TASK_EVENT_TYPES_UNSICHTBAR: readonly TaskEventType[] = ['echo']

/** `false` für Ereignisse, die es nur zur Abwehr gibt (AP 5.3). */
export function istSichtbar(type: TaskEventType): boolean {
  return !TASK_EVENT_TYPES_UNSICHTBAR.includes(type)
}

/**
 * **Die Ereignistypen, die einen Zielstatus tragen dürfen** – also `nach` in der Nutzlast.
 *
 * ## Warum das eine Liste braucht und kein Gefühl
 *
 * `status_nach` ist eine **generierte Spalte** aus `payload.nach`, und die Auswertungen
 * fragen **nur sie** ab – ohne den Typ anzusehen. `erledigtProTag` lautet im Kern
 * `WHERE status_nach = 'erledigt'`. Jedes Ereignis, das `nach` in die Nutzlast schreibt,
 * behauptet damit: *hier wurde der Status auf X gesetzt.* Wer das nebenbei tut, fälscht
 * das Daily.
 *
 * **Genau das ist passiert** (01.08.2026): der Rückschreib-Vermerk trug `nach: workStatus`,
 * gemeint als „das war das Ziel". Gezählt wurde er als Erledigung – ohne Person, und am
 * **Tag des Rückschreibens** statt am Tag der Entscheidung. Zwei Aufgaben, die Nils am
 * 31.07. erledigt hatte, erschienen im Daily am 01.08. Gefunden hat es der Test zur
 * Echo-Erkennung, der genau diese Eigenschaft prüfte – am Nachbarn.
 *
 * ## Warum `closed` und `reopened` dazugehören
 *
 * Weil sie es wirklich sind: ein `closed` aus dem NetCon-Feed **ist** der Abschluss, kein
 * Vermerk darüber. Sie auszuschließen hieße, die NetCon-Erledigungen aus dem Daily zu
 * nehmen – über 1.000 Ereignisse.
 */
export const TASK_EVENT_TYPES_MIT_ZIELSTATUS: readonly TaskEventType[] = [
  'status_changed',
  'closed',
  'reopened',
]

/** `true`, wenn dieses Ereignis behaupten darf, den Status gesetzt zu haben. */
export function darfZielstatusTragen(type: TaskEventType): boolean {
  return TASK_EVENT_TYPES_MIT_ZIELSTATUS.includes(type)
}

/**
 * Gruppierung für die Filter-Chips im Tages-Popup (Kap. 4.6): dort wird nach
 * „nur Erledigt" oder „alles inkl. Statuswechsel" gefiltert.
 */
export const TASK_EVENT_GROUPS = ['erledigt', 'status', 'kommentar', 'code'] as const
export type TaskEventGroup = (typeof TASK_EVENT_GROUPS)[number]

export const TASK_EVENT_GROUP_LABELS: Record<TaskEventGroup, string> = {
  erledigt: 'Erledigt',
  status: 'Statuswechsel',
  kommentar: 'Kommentare',
  code: 'Code',
}

/**
 * Ordnet einen Ereignistyp einer Filtergruppe zu. `null` heißt: erscheint im
 * Verlauf, ist aber kein eigener Filter (z. B. „Übernommen").
 */
export function eventGroupOf(type: TaskEventType): TaskEventGroup | null {
  switch (type) {
    case 'status_changed':
    case 'pulled':
    case 'assigned':
    case 'unassigned':
    case 'pool_changed':
    case 'priority_changed':
    case 'label_changed':
    case 'reopened':
      return 'status'
    case 'closed':
      return 'erledigt'
    case 'comment':
      return 'kommentar'
    case 'commit_referenced':
    case 'merge_request_referenced':
      return 'code'
    // Diese drei erscheinen im Verlauf einer Aufgabe, bekommen im Tages-Popup
    // aber keinen eigenen Filter-Chip: „jemand hat einen Titel korrigiert" ist
    // keine Kategorie, nach der im Daily gesucht wird.
    case 'imported':
    case 'created':
    case 'updated':
      return null
    // Wird gar nicht erst angezeigt (AP 5.3) – eine Filtergruppe wäre eine Einladung,
    // ihn doch irgendwo einzublenden.
    case 'echo':
      return null
    /**
     * **Ein Vorschlag ist kein Statuswechsel.** Er in die Gruppe „Statuswechsel" zu stecken
     * hieße, dass ein Wunsch des Fachbereichs im Tages-Popup neben tatsächlich geleisteter
     * Arbeit steht – und dort zählt, was geschehen ist, nicht was jemand gern hätte.
     */
    case 'prio_vorschlag':
      return null
    /**
     * **Ein Umzug ist keine geleistete Arbeit.** Er gehört in den Verlauf der Aufgabe – dort
     * beantwortet er „warum sehe ich das hier plötzlich" –, aber nicht in einen Filter-Chip des
     * Tages-Popups: dort steht, was jemand geschafft hat, und eine Aufgabe umzuhängen ist
     * Aufräumen, nicht Arbeit am Gegenstand.
     */
    case 'workspace_changed':
      return null
    /**
     * **Aus zwei mach eins ist Aufräumen, keine Leistung** – dieselbe Begründung wie beim Umzug.
     * Im Verlauf der Aufgabe ist die Zeile unentbehrlich (sie erklärt den fremden Verlauf), im
     * Tages-Popup wäre sie eine erledigte Aufgabe, die niemand erledigt hat.
     */
    case 'merged':
      return null
  }
}

/**
 * Filtergruppe eines **konkreten Ereignisses** – mit Blick auf den Zielstatus.
 *
 * `eventGroupOf` kann nur den Typ ansehen und ordnet deshalb `closed` der Gruppe
 * „Erledigt" zu. In den echten Daten trägt aber der **Statuswechsel** dieses
 * Signal: bei 79 % der GitLab-Issues wird `Status::Erledigt` gesetzt und das
 * Issue nie geschlossen ([F-02](../../../docs/OFFENE-FRAGEN.md)). Wer im
 * Tages-Popup auf „Erledigt" filtert und nur `closed` bekommt, sieht fast
 * nichts – und schließt daraus, es sei nichts passiert.
 *
 * Diese Funktion ist deshalb die richtige für alles, was Ereignisse aus dem Log
 * gruppiert; `eventGroupOf` bleibt für den Fall, dass nur der Typ vorliegt.
 */
export function eventGroupOfEvent(ereignis: {
  type: TaskEventType
  /** Zielstatus aus `payload.nach`, wie die generierte Spalte ihn liefert. */
  statusNach?: string | null
}): TaskEventGroup | null {
  if (ereignis.type === 'status_changed' && ereignis.statusNach === 'erledigt') {
    return 'erledigt'
  }
  return eventGroupOf(ereignis.type)
}
